import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { getDateKey } from '../games/WordMaster/wordMasterUtils';
import { advanceAfterPlay } from '../utils/calendarStreak';
import { updateLeaderboardEntry } from './leaderboardService';

function isOfflineError(err) {
  return err?.code === 'unavailable' || err?.message?.includes('offline');
}

function readGlobalStreakFields(data) {
  const current = (data.globalStreak ?? 0) | 0;
  const lastPlayed = data.lastPlayedGlobalDate ?? null;
  return { current, lastPlayed };
}

/**
 * After a win: update profile (fullCrosswordStats, global streak), leaderboards.
 * Mirrors Flutter `FullCrosswordRepository.recordCompletion`.
 */
export async function recordFullCrosswordCompletion(score, puzzleId) {
  const user = auth.currentUser;
  if (!user) return { ok: false, reason: 'auth' };
  try {
    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return { ok: false, reason: 'no_profile' };

    const data = snap.data();
    const xs = { ...(data.fullCrosswordStats || {}) };
    const prevBest = (xs.bestScore ?? 0) | 0;

    const playDate = getDateKey();
    const { current, lastPlayed } = readGlobalStreakFields(data);
    const global = advanceAfterPlay({
      playDate,
      lastPlayed,
      currentStreak: current,
    });
    const didAdvanceGlobal = global.streak > current || global.lastPlayed !== lastPlayed;

    if (score > prevBest) {
      xs.bestScore = score;
    }
    xs.lastCompletedPuzzleId = puzzleId;

    const displayName = data.displayName || user.displayName || user.email?.split('@')[0] || 'Player';

    await updateDoc(userRef, {
      fullCrosswordStats: xs,
      ...(didAdvanceGlobal
        ? {
            globalStreak: global.streak,
            lastPlayedGlobalDate: global.lastPlayed,
          }
        : {}),
      updatedAt: serverTimestamp(),
    });

    if (score > prevBest) {
      await updateLeaderboardEntry('full_crossword_best', user.uid, displayName, score);
    }
    if (didAdvanceGlobal) {
      await updateLeaderboardEntry('global_streak', user.uid, displayName, global.streak);
    }

    return { ok: true, improved: score > prevBest, score, bestScore: Math.max(prevBest, score) };
  } catch (err) {
    if (isOfflineError(err)) return { ok: false, reason: 'offline' };
    throw err;
  }
}
